import React, { useMemo } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  Skeleton,
  Alert,
} from "@mui/material";
import PageTitle from "../components/common/PageTitle";
import { useProducts } from "../hooks/useProducts";
import type { Product } from "../types";
import AppsIcon from "@mui/icons-material/Apps";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BarChartIcon from "@mui/icons-material/BarChart";
import StarIcon from "@mui/icons-material/Star";

// Mock top subscribers list until backend API exists
const MOCK_TOP_SUBSCRIBERS: { name: string; product: string; requests: number }[] = [
  { name: "Acme Corp", product: "Payment API", requests: 1240000 },
  { name: "TechStart Inc", product: "Auth API", requests: 892000 },
  { name: "DataFlow LLC", product: "Payment API", requests: 756000 },
  { name: "CloudNine", product: "Notifications API", requests: 534000 },
  { name: "MobileFirst", product: "Auth API", requests: 421000 },
];

function mockTrafficForProduct(product: Product, index: number, total: number): number {
  return 50000 * (total - index) + (product.uuid.length % 20000);
}

function mockSubscribersForProduct(product: Product): number {
  return 5 + (product.uuid.length % 45);
}

const AnalyticsDashboardPage: React.FC = () => {
  const { products, loading, error } = useProducts();

  const activeProducts = useMemo(
    () => products.filter((p) => p.status === "ACTIVE"),
    [products]
  );

  const productStats = useMemo(
    () =>
      activeProducts.map((p, i) => ({
        product: p,
        traffic: mockTrafficForProduct(p, i, activeProducts.length),
        subscribers: mockSubscribersForProduct(p),
      })),
    [activeProducts]
  );

  const mostPopular = useMemo(
    () => [...productStats].sort((a, b) => b.traffic - a.traffic).slice(0, 10),
    [productStats]
  );

  const trafficMax = useMemo(
    () => Math.max(1, ...productStats.map((x) => x.traffic)),
    [productStats]
  );

  const totalTraffic = useMemo(
    () => productStats.reduce((a, x) => a + x.traffic, 0),
    [productStats]
  );
  const totalSubscribers = useMemo(
    () => productStats.reduce((a, x) => a + x.subscribers, 0),
    [productStats]
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="Insights"
        pageDescription="API Product Manager Dashboard: product apps, subscribers, traffic insights, and most popular products."
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Traffic insights summary cards */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Traffic insights for product managers
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <BarChartIcon color="primary" />
                  <Typography color="text.secondary" variant="body2">
                    Total API requests
                  </Typography>
                </Box>
                {loading ? (
                  <Skeleton width={80} height={36} />
                ) : (
                  <Typography variant="h4" fontWeight={700}>
                    {(totalTraffic / 1_000_000).toFixed(2)}M
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <PeopleIcon color="primary" />
                  <Typography color="text.secondary" variant="body2">
                    Total subscribers
                  </Typography>
                </Box>
                {loading ? (
                  <Skeleton width={60} height={36} />
                ) : (
                  <Typography variant="h4" fontWeight={700}>
                    {totalSubscribers}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <TrendingUpIcon color="primary" />
                  <Typography color="text.secondary" variant="body2">
                    Active products
                  </Typography>
                </Box>
                {loading ? (
                  <Skeleton width={40} height={36} />
                ) : (
                  <Typography variant="h4" fontWeight={700}>
                    {activeProducts.length}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Product manager apps */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Product manager apps
        </Typography>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {loading ? (
            [1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card><CardContent><Skeleton height={80} /></CardContent></Card>
              </Grid>
            ))
          ) : activeProducts.length === 0 ? (
            <Grid item xs={12}>
              <Typography color="text.secondary">No active products yet.</Typography>
            </Grid>
          ) : (
            activeProducts.slice(0, 8).map((product) => (
              <Grid item xs={12} sm={6} md={3} key={product.uuid}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <AppsIcon sx={{ color: "primary.main", mt: 0.25 }} fontSize="small" />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {product.description || "—"}
                        </Typography>
                        <Chip label={product.status} size="small" sx={{ mt: 1 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        {/* Most popular products */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Most popular products
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="right">Subscribers</TableCell>
                <TableCell align="right">Traffic (requests)</TableCell>
                <TableCell align="right">Share of traffic</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton width={120} /></TableCell>
                    <TableCell align="right"><Skeleton width={40} /></TableCell>
                    <TableCell align="right"><Skeleton width={60} /></TableCell>
                    <TableCell align="right"><Skeleton width={50} /></TableCell>
                  </TableRow>
                ))
              ) : (
                mostPopular.map(({ product, traffic, subscribers }) => (
                  <TableRow key={product.uuid}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <StarIcon sx={{ color: "warning.main", fontSize: 18 }} />
                        {product.name}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{subscribers}</TableCell>
                    <TableCell align="right">
                      {(traffic / 1000).toFixed(0)}K
                    </TableCell>
                    <TableCell align="right">
                      <LinearProgress
                        variant="determinate"
                        value={(traffic / trafficMax) * 100}
                        sx={{ minWidth: 60, height: 8, borderRadius: 1 }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Product subscribers (count by product) */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Product subscribers
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="right">Subscribers</TableCell>
                <TableCell align="right">Traffic (requests)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [1, 2, 3, 4].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton width={140} /></TableCell>
                    <TableCell align="right"><Skeleton width={50} /></TableCell>
                    <TableCell align="right"><Skeleton width={70} /></TableCell>
                  </TableRow>
                ))
              ) : (
                mostPopular.map(({ product, traffic, subscribers }) => (
                  <TableRow key={product.uuid}>
                    <TableCell>{product.name}</TableCell>
                    <TableCell align="right">{subscribers}</TableCell>
                    <TableCell align="right">{(traffic / 1000).toFixed(0)}K</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Most subscribers with highest traffic */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Most subscribers with the highest traffic
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Subscriber</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Requests</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {MOCK_TOP_SUBSCRIBERS.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.product}</TableCell>
                  <TableCell align="right">
                    {(row.requests / 1000).toFixed(0)}K
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Box>
  );
};

export default AnalyticsDashboardPage;
